import { computed, defineComponent, h } from 'vue'

import { bsComponentProps, useBsComponent } from '../useBsComponent'
import { useVm } from '../../../composables/useVm'
import { domHandlers } from '../quasarHandlers'

/**
 * A rounded label. Takes the bundle's listeners so a click still bubbles, and nothing else — there is
 * nothing here to validate or save.
 *
 * `color` accepts an object as well as a string, because a caller may hand it a computed class map; the
 * first truthy key wins.
 */
export default defineComponent({
  name: 'BsChip',

  props: {
    ...bsComponentProps,
    color: { type: [String, Object] as unknown as () => string | object, default: void 0 },
    textColor: { type: String, default: void 0 }
  },

  /**
   * Declared, which Vue 2 never required. Two things follow from it, and both matter here:
   *
   *  - a native `input` on the root element is no longer ALSO reported as a component emission. Left
   *    undeclared, the bundle's own `emit('input')` and the DOM event both counted, so a single
   *    keystroke looked like two.
   *  - `onInput` from a parent stops falling through to the root element as a native listener and
   *    becomes a component-event handler, which is what a caller of this bundle means by it.
   */
  emits: ['input'],

  setup (props, { emit }) {
    const vm = useVm()
    const bs = useBsComponent({ vm, props, emit })

    const localClasses = computed(() => {
      let result = ''

      if (props.textColor !== void 0) {
        result += ` text-${props.textColor}`
      } else {
        result += ' text-black'
      }

      if (props.color !== void 0) {
        if (typeof props.color === 'object') {
          const indexedColor: { [index: string]: any } = props.color
          const color = Object.keys(indexedColor).find(key => indexedColor[key] === true)
          result += ` bg-${color}`
        } else {
          result += ` bg-${props.color}`
        }
      }

      return result
    })

    return {
      ...bs,
      localClasses
    }
  },

  /**
   * Vue 3 render conventions, applied here and throughout the bundle:
   *
   *  - `h` is imported, not a parameter.
   *  - there is no `staticClass`; a static and a dynamic class merge into one array.
   *  - `on: { click: fn }` is flat `onClick` handlers. `domHandlers()` does that mapping for a
   *    listener object assembled at runtime, which is what `getListeners()` returns.
   */
  render () {
    return h('span', {
      class: ['bs-chip', this.localClasses],
      ...domHandlers(this.getListeners())
    }, this.slot(this, 'default'))
  }
})
